/**
 * StudentDashboard - the home screen for a student
 * ------------------------------------------------
 * Earnings and rating at the top, jobs coming up (with the 24-hour cancel
 * rule made obvious), then every application split by status.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Star,
  AlertTriangle,
  ShieldAlert,
  CalendarCheck,
  Briefcase,
  MessageSquare,
} from 'lucide-react';

import { studentApi, applicationApi, reviewApi, safetyApi, offerApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { money, dateTime, countdown } from '../utils/format';
import { choreWhenShort } from '../utils/schedule';
import { ApplicationBadge } from '../components/Badge';
import Badge from '../components/Badge';
import StarRating from '../components/StarRating';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import GuardianConsentBanner from '../components/GuardianConsentBanner';
import ConfirmEmailBanner from '../components/ConfirmEmailBanner';
import LeaveUnsafeDialog from '../components/LeaveUnsafeDialog';
import BookingRequests from '../components/BookingRequests';

import { useI18n } from '../hooks/useI18n';
import { useFeatures } from '../hooks/useFeatures';

/**
 * Only the three that are still live, at the owner's request (18 September
 * 2026). "Not chosen" and "Withdrawn" were two lists of things that are not
 * going to happen - nothing to do, nothing to decide, and a standing reminder
 * of every job somebody did not get. The applications themselves are still in
 * the database, and the student was told at the time either way.
 *
 * Work that is DONE is not here either: it has its own section further down,
 * where the two things still worth doing about it live - messaging the person
 * and telling a moderator something went wrong.
 */
const TABS = [
  { key: 'accepted', label: 'tabs.gotTheJob' },
  { key: 'backup', label: 'tabs.backup' },
  { key: 'pending', label: 'tabs.waitingToHear' },
];

export default function StudentDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { offersEnabled } = useFeatures();

  const [data, setData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  /**
   * Requests somebody has made to book this student, from their published
   * availability. They are decided on "My offer" as well, but the
   * notification - "Someone would like to book you" - sends people HERE, and
   * until 24 September 2026 this page said nothing about them at all.
   */
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState('accepted');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [cancelling, setCancelling] = useState(null); // the application being cancelled
  const [cancelReason, setCancelReason] = useState('');

  /**
   * Inside 24 hours a student may now cancel and must say why - the same rule
   * and the same wording as the chore page. Before 25 August 2026 they simply
   * could not, which left somebody who genuinely could not go with a booking
   * they could not release and a poster who was never told.
   */
  const needsCancelReason =
    cancelling?.status === 'accepted'
    && Number(cancelling?.chore?.hoursUntilStart) < 24
    && !cancelling?.freeCancellation;
  // Matches MIN_LATE_CANCEL_REASON on the server, which is what enforces it.
  const cancelReasonTooShort = cancelReason.trim().length < 15;
  // { app, chore } while somebody is leaving a job they do not feel safe at.
  // Separate from `cancelling` because it is a different thing entirely - see
  // components/LeaveUnsafeDialog.jsx.
  const [leaving, setLeaving] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Booking requests only exist while offers are switched on (archived
      // 17 October 2026 - hooks/useFeatures.js). Asking for them while they
      // are off would get a refusal, and one refusal inside Promise.all
      // throws away the whole dashboard with it.
      const [dashboard, mine, pending, asked] = await Promise.all([
        studentApi.dashboard(),
        applicationApi.mine(),
        reviewApi.pending(),
        offersEnabled ? offerApi.requests() : Promise.resolve({ requests: [] }),
      ]);
      setData(dashboard);
      setApplications(mine.applications);
      setPendingReviews(pending.chores);
      setRequests(asked.requests || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [offersEnabled]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async () => {
    setBusy(true);
    try {
      const result = await applicationApi.cancelJob(cancelling._id, cancelReason.trim());
      setNotice(
        result.promotedBackup
          ? `Cancelled. ${result.promotedBackup.fullName} was the backup and has taken the job on.`
          : 'Cancelled. The chore has gone back on the marketplace.'
      );
      setCancelling(null);
      setCancelReason('');
      await load();
    } catch (err) {
      setError(err.message);
      setCancelling(null);
    } finally {
      setBusy(false);
    }
  };

  /**
   * The same button, two different actions, decided by whether the job has
   * happened yet.
   *
   * ON A JOB STILL TO COME it is the way out: the student is un-booked
   * immediately and a report goes to the moderators. ON A FINISHED ONE there
   * is nothing to leave, so it is a report and only a report - the server
   * refuses the other one now (routes/applications.js), because un-booking a
   * completed job would reopen it and tell the poster the student had
   * dropped out.
   *
   * Both land in the same queue, marked urgent, and neither tells the poster
   * that anything was said.
   */
  const handleLeaveUnsafe = async (reason) => {
    setBusy(true);
    try {
      if (leaving.done) {
        await safetyApi.report({
          about: leaving.chore?.postedBy?._id,
          chore: leaving.chore?._id,
          reason: 'made_me_uncomfortable',
          details: reason,
        });
        setNotice(t('dashboard.reportSent'));
      } else {
        const result = await applicationApi.leaveUnsafe(leaving.app._id, reason);
        setNotice(result.message);
      }
      setLeaving(null);
      await load();
    } catch (err) {
      setError(err.message);
      setLeaving(null);
    } finally {
      setBusy(false);
    }
  };

  /** Accept or turn down a request to book this student. */
  const respondToRequest = async (id, accept) => {
    setError('');
    try {
      if (accept) {
        await offerApi.accept(id);
        setNotice(t('myOffer.accepted'));
      } else {
        await offerApi.decline(id);
        setNotice(t('myOffer.declined'));
      }
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleWithdraw = async (applicationId) => {
    try {
      await applicationApi.withdraw(applicationId);
      setNotice(t('dashboard.applicationWithdrawn'));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <Loader label={t('dashboard.loadingStudent')} />;

  const stats = data?.stats || {};

  /**
   * A finished job keeps its application - the student WAS accepted, and that
   * is how the site remembers who did it. So "Got the job" has to say "and it
   * has not happened yet", or a chore done in July sits at the top of the list
   * of things to turn up to.
   */
  const isDone = (app) => app.chore?.status === 'completed';
  /**
   * And nothing whose chore is over in some other way: cancelled, or closed
   * because nobody ever marked it done (utils/expiry.js). Those are not
   * applications anybody is waiting on, and leaving them in "Got the job"
   * is what made a job from three weeks ago read as current work.
   */
  const stillLive = (app) => ['open', 'assigned'].includes(app.chore?.status);
  const live = applications.filter((app) => !isDone(app) && stillLive(app));
  const finished = applications
    .filter((app) => isDone(app) && app.status === 'accepted')
    .sort((a, b) => new Date(b.chore?.completedAt || 0) - new Date(a.chore?.completedAt || 0));

  const visible = live.filter((app) => app.status === tab);
  const countFor = (status) => live.filter((a) => a.status === status).length;

  return (
    <div className="container section">
      <div className="row-between page-header">
        <div>
          <h1>{t('dashboard.hello', { name: user.firstName })}</h1>
          <p>{t('dashboard.studentSubtitle')}</p>
        </div>
        <Link to="/marketplace" className="btn btn-primary btn-inline">
          <Search size={18} /> {t('dashboard.findMoreWork')}
        </Link>
      </div>

      <ConfirmEmailBanner />
      <GuardianConsentBanner />

      {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-sm)' }}>{notice}</div>}
      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>{error}</div>}

      {/* ---------------- SOMEBODY WANTS TO BOOK YOU ---------------- */}
      {offersEnabled && <BookingRequests requests={requests} onRespond={respondToRequest} />}

      {/* ---------------- NUMBERS ---------------- */}
      <div className="stat-grid" style={{ marginBottom: 'var(--space-lg)' }}>
        <div className="stat stat-blue">
          <div className="stat-value">{stats.choresCompleted || 0}</div>
          <div className="stat-label">{t('dashboard.choresCompleted')}</div>
        </div>
        <div className="stat stat-amber">
          <div className="stat-value">{stats.applications?.pending || 0}</div>
          <div className="stat-label">{t('dashboard.waitingToHear')}</div>
        </div>
        <div className="stat">
          <div className="stat-value" style={{ fontSize: 'var(--text-h3)' }}>
            <StarRating
              value={stats.rating?.average || 0}
              count={stats.rating?.count || 0}
              size={18}
            />
          </div>
          <div className="stat-label">{t('dashboard.yourRating')}</div>
        </div>
      </div>

      {/* ---------------- REVIEWS TO WRITE ---------------- */}
      {pendingReviews.length > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-lg)', borderColor: 'var(--color-amber)' }}>
          <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('dashboard.reviewsToLeave')}</h3>
          <div className="stack" style={{ gap: 'var(--space-xs)' }}>
            {pendingReviews.map((chore) => (
              <div className="row-between" key={chore._id}>
                <span className="row" style={{ gap: 8 }}>
                  <Star size={18} className="text-amber" aria-hidden="true" />
                  <span>
                    Review {chore.postedBy?.firstName} for &ldquo;{chore.title}&rdquo;
                  </span>
                </span>
                <Link to={`/chores/${chore._id}`} className="btn btn-outline btn-sm btn-inline">
                  {t('dashboard.leaveAReview')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- UPCOMING JOBS ---------------- */}
      {data?.upcomingJobs?.length > 0 && (
        <section style={{ marginBottom: 'var(--space-lg)' }}>
          <h2 style={{ fontSize: 'var(--text-h3)', marginBottom: 'var(--space-sm)' }}>
            <CalendarCheck size={22} style={{ display: 'inline', verticalAlign: -3 }} />{' '}
            {t('dashboard.jobsComingUp')}
          </h2>

          <div className="stack" style={{ gap: 'var(--space-xs)' }}>
            {data.upcomingJobs.map((job) => (
              <div className="card" key={job._id}>
                <div className="row-between">
                  <div>
                    <Link to={`/chores/${job._id}`} className="card-title">
                      {job.title}
                    </Link>
                    <div className="card-meta" style={{ marginTop: 4 }}>
                      {/* On a multi-day booking the date is the NEXT visit,
                          not the job. Saying so here matters more than
                          anywhere else on the site: this is the screen a
                          student checks to see what they have agreed to. */}
                      <span>
                        {job.isMultiDay
                          ? `next ${choreWhenShort(job)} · ${job.visitCount} visits`
                          : choreWhenShort(job)}
                      </span>
                      <span>{countdown(job.hoursUntilStart)}</span>
                      <span>{job.location?.city}</span>
                      <span className="pay-amount">
                        {money(job.isMultiDay ? job.totalPay : job.pay)}
                      </span>
                    </div>
                  </div>

                  <div className="row" style={{ gap: 'var(--space-xs)' }}>
                    {job.isBackup ? <Badge tone="soft-blue">{t('dashboard.youAreBackup')}</Badge> : <Badge tone="green">{t('dashboard.yours')}</Badge>}
                    {/* It USED to say "cannot cancel", which stopped being
                        true on 25 August 2026 - a student can cancel inside
                        24 hours now, and has to say why. */}
                    {job.hoursUntilStart < 24 && !job.isBackup && (
                      <Badge tone="amber" icon={AlertTriangle}>
                        {t('dashboard.startsSoon')}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- WORK YOU HAVE DONE ---------------- */}
      {/* Its own section, at the owner's request (18 September 2026), rather
          than sitting in "Got the job" among the jobs still to turn up to.
          Two things are still worth doing about a finished job, and both are
          here: talk to the person, and tell a moderator if something was
          wrong. Neither used to survive the job being marked done in any
          obvious place. */}
      {finished.length > 0 && (
        <section style={{ marginBottom: 'var(--space-lg)' }}>
          <h2 style={{ fontSize: 'var(--text-h3)', marginBottom: 'var(--space-sm)' }}>
            <Briefcase size={22} style={{ display: 'inline', verticalAlign: -3 }} />{' '}
            {t('dashboard.workYouHaveDone')}
          </h2>

          <div className="stack" style={{ gap: 'var(--space-xs)' }}>
            {finished.map((app) => {
              const chore = app.chore;
              return (
                <div className="card" key={app._id}>
                  <div className="row-between">
                    <div>
                      <Link to={`/chores/${chore._id}`} className="card-title">
                        {chore.title}
                      </Link>
                      <div className="card-meta" style={{ marginTop: 4 }}>
                        <span>{chore.completedAt ? dateTime(chore.completedAt) : choreWhenShort(chore)}</span>
                        <span>{chore.location?.city}</span>
                        {chore.postedBy && (
                          <span>
                            {chore.postedBy.firstName} {chore.postedBy.lastName?.charAt(0)}.
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="pay-amount">{money(chore.pay)}</span>
                  </div>

                  <div className="dashboard-job-actions">
                    <Link to={`/messages/${chore._id}`} className="btn btn-outline btn-sm btn-inline">
                      <MessageSquare size={15} aria-hidden="true" />
                      {t('dashboard.messageName', { name: chore.postedBy?.firstName || '' })}
                    </Link>
                    {/* Same button, same words, different action - see
                        handleLeaveUnsafe. There is nothing to leave now, so
                        this tells a moderator what happened. */}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-inline dashboard-unsafe-btn"
                      onClick={() => setLeaving({ app, chore, done: true })}
                    >
                      <ShieldAlert size={15} aria-hidden="true" />
                      {t('dashboard.doNotFeelSafe')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------------- APPLICATIONS ---------------- */}
      <h2 style={{ fontSize: 'var(--text-h3)', marginBottom: 'var(--space-sm)' }}>{t('dashboard.yourApplications')}</h2>

      <div className="tabs">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`tab${tab === item.key ? ' active' : ''}`}
            onClick={() => setTab(item.key)}
          >
            {t(item.label)}
            <span className="tab-count">{countFor(item.key)}</span>
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title={t('dashboard.nothingYet')}
          message={
            tab === 'pending'
              ? 'Applications you are waiting to hear back about will appear here.'
              : tab === 'accepted'
                ? 'Jobs you have been accepted for will show up here.'
                : 'Nothing in this list.'
          }
          action={
            <Link to="/marketplace" className="btn btn-primary btn-inline">
              <Search size={18} /> {t('nav.browse')}
            </Link>
          }
        />
      ) : (
        <div className="stack" style={{ gap: 'var(--space-sm)' }}>
          {visible.map((app) => {
            const chore = app.chore;
            return (
              <div className="card" key={app._id}>
                <div className="card-header">
                  <Link to={`/chores/${chore._id}`} className="card-title">
                    {chore.title}
                  </Link>
                  <div className="row" style={{ gap: 'var(--space-xs)' }}>
                    <span className="pay-amount">{money(chore.pay)}</span>
                    <ApplicationBadge status={app.status} />
                  </div>
                </div>

                <div className="card-meta">
                  <span>{choreWhenShort(chore)}</span>
                  <span>{chore.location?.city}</span>
                  <span>{chore.category}</span>
                  {chore.postedBy && (
                    <span>
                      {chore.postedBy.firstName} {chore.postedBy.lastName?.charAt(0)}.
                    </span>
                  )}
                </div>

                {app.message && (
                  <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
                    Your message: &ldquo;{app.message}&rdquo;
                  </p>
                )}

                {/* ---- The 24-hour rule, spelled out ---- */}
                {app.status === 'accepted' && chore.hoursUntilStart > 0 && (
                  <div className="card-footer">
                    {/* The button is here whether or not the job is close.
                        It used to disappear inside 24 hours, next to a line
                        saying cancelling was not possible - which stopped
                        being true on 25 August 2026, and while it was there
                        a student who could not go had no way to say so. */}
                    <span className={`text-sm${app.cancelNeedsReason ? '' : ' text-muted'}`}>
                      {app.cancelNeedsReason ? (
                        <>
                          <AlertTriangle size={14} aria-hidden="true" style={{ verticalAlign: -2 }} />{' '}
                          {t('dashboard.cancelLateNote', { when: countdown(chore.hoursUntilStart) })}
                        </>
                      ) : (
                        t('dashboard.cancelStillFine', { when: countdown(chore.hoursUntilStart) })
                      )}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm btn-inline"
                      onClick={() => setCancelling(app)}
                    >
                      {t('dashboard.cancelThisJob')}
                    </button>
                  </div>
                )}

                {/* ---- The way out ----
                    OUTSIDE the block above on purpose, so it is there whether
                    or not the 24-hour window has closed. The moment a student
                    most needs this is the moment ordinary cancelling stops
                    working: standing at the door. Kept quiet in appearance -
                    it is not an action anybody should feel nudged towards -
                    but always present, and never behind a menu. */}
                {app.status === 'accepted' && (
                  <div className="dashboard-job-actions">
                    {/* The conversation with the poster, from the job itself.
                        It used to be reachable only from the navbar's speech
                        bubble, which a student promoted from backup at short
                        notice had no reason to know about (16 September 2026). */}
                    {['assigned', 'completed'].includes(chore.status) && (
                      <Link to={`/messages/${chore._id}`} className="btn btn-outline btn-sm btn-inline">
                        <MessageSquare size={15} aria-hidden="true" />
                        {t('dashboard.messageName', { name: chore.postedBy?.firstName || '' })}
                      </Link>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-inline dashboard-unsafe-btn"
                      onClick={() => setLeaving({ app, chore })}
                    >
                      <ShieldAlert size={15} aria-hidden="true" />
                      {t('dashboard.doNotFeelSafe')}
                    </button>
                  </div>
                )}

                {app.status === 'backup' && (
                  <div className="card-footer">
                    <span className="text-sm text-muted">
                      {t('dashboard.standingBy')}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-inline"
                      onClick={() => setCancelling(app)}
                    >
                      {t('dashboard.stepDown')}
                    </button>
                  </div>
                )}

                {app.status === 'pending' && (
                  <div className="card-footer">
                    <span className="text-sm text-muted">{t('dashboard.awaitingDecision')}</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-inline"
                      onClick={() => handleWithdraw(app._id)}
                    >
                      {t('dashboard.withdraw')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <LeaveUnsafeDialog
        open={Boolean(leaving)}
        chore={leaving?.chore}
        busy={busy}
        onClose={() => setLeaving(null)}
        onConfirm={handleLeaveUnsafe}
        done={Boolean(leaving?.done)}
      />

      {/* ---------------- CANCEL CONFIRMATION ---------------- */}
      <Modal
        open={Boolean(cancelling)}
        onClose={() => setCancelling(null)}
        title={
          cancelling?.status === 'backup'
            ? t('dashboard.stepDownQ')
            : t('dashboard.cancelJobQ')
        }
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setCancelling(null)}>
              {t('dashboard.keepIt')}
            </button>
            <button
              type="button"
              className="btn btn-danger btn-inline"
              onClick={handleCancel}
              disabled={busy || (needsCancelReason && cancelReasonTooShort)}
            >
              {busy ? t('choreDetail.cancelling') : t('choreDetail.yesCancel')}
            </button>
          </>
        }
      >
        <p>
          <strong>{cancelling?.chore?.title}</strong>
        </p>
        <p className="text-muted" style={{ marginTop: 'var(--space-xs)' }}>
          {cancelling?.status === 'backup'
            ? 'You will no longer be the backup for this chore.'
            : 'The person who posted it will be told straight away. If they named a backup, that student takes over automatically.'}
        </p>
        {cancelling?.status === 'accepted' && (
          <div className="alert alert-warning" style={{ marginTop: 'var(--space-sm)' }}>
            <AlertTriangle size={18} aria-hidden="true" />
            <span className="text-sm">
              {t('dashboard.cancellingRecorded')}
            </span>
          </div>
        )}

        {/* Only when it is actually late. The same box, and the same rule, as
            the chore page - see ChoreDetail.jsx. */}
        {needsCancelReason && (
          <div className="field" style={{ marginTop: 'var(--space-sm)' }}>
            <label htmlFor="dash-cancel-reason">{t('choreDetail.whyCancelling')} *</label>
            <p className="hint" style={{ marginTop: 0 }}>
              {t('choreDetail.whyCancellingHint', { name: cancelling?.chore?.postedBy?.firstName || '' })}
            </p>
            <textarea
              id="dash-cancel-reason"
              rows={3}
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              maxLength={1000}
              placeholder={t('choreDetail.whyCancellingPlaceholder')}
              aria-invalid={cancelReasonTooShort}
            />
            {cancelReasonTooShort && (
              <span className="hint">{t('choreDetail.whyCancellingShort')}</span>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
